import { useState, useEffect } from 'react';
import type { AdminAlbum, PhotoCategory } from '@/mocks/adminPhotos';
import { categoryLabels } from '@/mocks/adminPhotos';
import { SwalAlert } from '@/hooks/useSweetAlert';

interface AlbumManagerProps {
  open: boolean;
  album: AdminAlbum | null;
  categories: PhotoCategory[];
  onClose: () => void;
  onSave: (data: { name: string; category: PhotoCategory }) => void;
}

export default function AlbumManager({ open, album, categories, onClose, onSave }: AlbumManagerProps) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<PhotoCategory>('eventos');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (album) {
      setName(album.name);
      setCategory(album.category);
    } else {
      setName('');
      setCategory('eventos');
    }
  }, [album, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('El nombre del álbum es requerido');
      return;
    }
    setSaving(true);
    onSave({ name: name.trim(), category });
    setSaving(false);
    await SwalAlert.toast(album ? 'Álbum actualizado.' : 'Álbum creado.', 'success');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>

      <div className="relative bg-background-50 rounded-2xl w-full max-w-md overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <h2 className="text-base font-bold text-foreground-950">
            {album ? 'Editar álbum' : 'Crear álbum'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-5 space-y-4">
            {/* Nombre */}
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                Nombre del álbum <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setError(''); }}
                required
                maxLength={80}
                placeholder="Ej: Fotos del Festival 2026"
                className={`w-full px-3.5 py-2.5 rounded-xl border bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all ${error ? 'border-red-400 bg-red-50/50' : 'border-background-200'}`}
              />
              {error && <p className="text-red-500 text-[10px] mt-1">{error}</p>}
            </div>

            {/* Categoría */}
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                Categoría <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-1.5">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all border ${
                      category === cat
                        ? 'bg-primary-50 text-primary-700 border-primary-300'
                        : 'bg-background-50 text-foreground-500 border-background-200 hover:border-background-300'
                    }`}
                  >
                    {categoryLabels[cat]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 px-5 py-4 border-t border-background-200 bg-background-100/70">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="flex items-center justify-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Guardando...
                </span>
              ) : (
                album ? 'Guardar cambios' : 'Crear álbum'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}